import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './d-edit-data-item-application.js';
import './d-edit-data-item-asset.js';
import '../../../../library/editors/elements/d-add-remove-buttons.js';
import type { InfosecCategory } from '../infosecCategory';
import type { InfosecDataType } from '../infosecDataType';
import type { InfosecDataItem } from '../infosecDataItem';
import type { InfosecStorageUnit } from '../infosecStorageUnit';
import type { InfosecAsset } from '../infosecAsset';
import type { SelectDropdownOption } from '../../../../library/editors/elements/select-dropdown-option';

/**
 * Given a data type and a category, renders filtered the data items. The data items are rendered using
 * different elements based on the category. This element does not handle the 'accounting' or 'emailAccounts' categories.
 *
 * The element also handles add / remove options for the data items
 *
 * @fires data-items-removed
 *
 * The other events are fired by the individual editing elements.
 *
 *
 */
@customElement('d-edit-data-items')
export class DEditDataItems extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
      }
      div {
        display: flex;
      }
      div + div {
        margin-top: 12px;
      }
      d-edit-data-item-application,
      d-edit-data-item-asset {
        flex: 1;
        display: block;
        box-sizing: border-box;
        border: 1px solid var(--borderColorOnGray);
        border-radius: 12px;
        background-color: var(--map-element-background-color);
        padding: 8px 12px 4px 12px;
      }
      d-edit-data-item-application + d-edit-data-item-application,
      d-edit-data-item-asset + d-edit-data-item-asset {
        margin-top: 12px;
      }
      d-edit-data-item-asset,
      d-edit-data-item-computer,
      d-edit-data-item-remote-server,
      d-edit-data-item-location {
        flex: 1;
      }
    `,
  ];
  @property({ type: Object })
  dataType!: InfosecDataType;
  @property({ type: Object })
  category!: InfosecCategory;
  @property({ type: Array })
  dataItems: InfosecDataItem[] = [];
  @property({ type: Array })
  cloudServices: InfosecStorageUnit[] = [];
  @property({ type: Array })
  remoteServers: InfosecStorageUnit[] = [];
  @property({ type: Array })
  computers: InfosecStorageUnit[] = [];
  @property({ type: Array })
  partners: SelectDropdownOption[] = [];
  @property({ type: Array })
  assets: InfosecAsset[] = [];

  private get dataItemsForCategory(): InfosecDataItem[] {
    const dataItems: InfosecDataItem[] = this.dataItems.filter((m) => {
      return m.dataType === this.dataType.type && m.category === this.category.category;
    });
    if (dataItems.length === 0) {
      const item = this.newDataItem;
      dataItems.push(item);
      console.log('Adding for ' + this.dataType.type + ' and ' + this.category.category, item);
    }
    return dataItems;
  }

  private get newDataItem(): InfosecDataItem {
    return {
      uuid: '',
      name: '',
      dataType: this.dataType.type,
      category: this.category.category,
      personalData: false,
      dataProcessor: '',
      application: '',
      storageUnit: '',
      storageUnitType: '',
      storageLocation: '',
      accessingEmployees: [],
      accessingPartners: [],
      assets: [],
      backupFrequency: '',
      backupResponsible: '',
      noBackupReason: '',
      createdDateTime: '',
    };
  }

  _removeDataItem(index) {
    const infosecDataItem = this.dataItems[index];
    const uuid = infosecDataItem.uuid;
    // this.dataItems.splice(index, 1);
    //  this._update('dataItems');
    this.dispatchEvent(
      new CustomEvent<{ uuid: string; dataType: string; category: string }>('data-items-removed', {
        bubbles: true,
        composed: true,
        detail: { uuid, dataType: infosecDataItem.dataType, category: infosecDataItem.category },
      }),
    );
  }

  _addDataItem() {
    const newDataItem = this.newDataItem;
    console.log('Adding', newDataItem);
    this.dataItems = [...this.dataItems, { ...newDataItem }];
  }

  render() {
    if (this.dataItems.length === 0) {
      this._addDataItem();
    }
    return html`
      ${this.dataItemsForCategory.map((dataItem, index) => {
        return html`
          <div>
            ${this.category.category === 'assetData'
              ? html`
                  <d-edit-data-item-asset
                    light-label
                    small-label
                    .dataItem=${dataItem}
                    .dataType=${this.dataType}
                    .category=${this.category}
                    .assets=${this.assets}
                    .computers=${this.computers}
                  ></d-edit-data-item-asset>
                `
              : nothing}
            <d-add-remove-buttons
              ?showRemove=${dataItem.uuid !== ''}
              ?showAdd=${dataItem.uuid !== '' && index === this.dataItemsForCategory.length - 1}
              @remove=${() => this._removeDataItem(index)}
              @add=${() => this._addDataItem()}
            ></d-add-remove-buttons>
          </div>
        `;
      })}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-data-items': DEditDataItems;
  }
}
